import { useId, type ChangeEvent, type ReactNode } from "react";
import { AccordionItem } from "../Accordion.tsx";
import CheckboxStandalone from "../CheckboxStandalone.tsx";
import CountBadge from "../CountBadge.tsx";
import styles from "./FilterSection.module.css";
import Text from "../Text.tsx";
// @ts-expect-error pure js
import TextInputStandalone from "../TextInputStandalone";
import {
  MultiSelectDropdownStandalone,
  type MultiSelectOption,
} from "../MultiSelectDropdown.tsx";

interface FilterOption {
  value: string;
  label: ReactNode;
}

interface FilterSectionProps {
  title: string;
  options: FilterOption[];
  values: string[];
  onChange: (values: string[]) => void;
  defaultOpen?: boolean;
}

export default function FilterSection({
  title,
  options,
  values,
  onChange,
}: FilterSectionProps) {
  function toggle(value: string) {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  }

  return (
    <AccordionItem
      variant="plain"
      value={title}
      title={
        <>
          {title}
          {values.length > 0 && <CountBadge count={values.length} />}
        </>
      }
    >
      <div className={styles.list}>
        {options.map((opt) => (
          <CheckboxStandalone
            key={opt.value}
            className={styles.option}
            checked={values.includes(opt.value)}
            onChange={() => toggle(opt.value)}
          >
            {opt.label}
          </CheckboxStandalone>
        ))}
      </div>
    </AccordionItem>
  );
}

interface FilterSelectSectionProps {
  title: string;
  options: MultiSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  defaultOpen?: boolean;
}

export function FilterSelectSection({
  title,
  options,
  values,
  onChange,
  placeholder = "Tous",
}: FilterSelectSectionProps) {
  const selected = options.filter((opt) => values.includes(opt.value));

  return (
    <AccordionItem
      variant="plain"
      value={title}
      title={
        <>
          {title}
          {values.length > 0 && <CountBadge count={values.length} />}
        </>
      }
    >
      <MultiSelectDropdownStandalone
        label={title}
        hiddenLabel
        options={options}
        value={selected}
        placeholder={placeholder}
        onChange={(selectedOptions: MultiSelectOption[]) =>
          onChange(selectedOptions.map((opt) => opt.value))
        }
      />
    </AccordionItem>
  );
}

interface FilterRangeSectionProps {
  title: string;
  minLabel?: string;
  maxLabel?: string;
  minValue: string;
  maxValue: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  inputType?: "number" | "date";
  unit?: string;
  defaultOpen?: boolean;
}

export function FilterRangeSection({
  title,
  minLabel = "Min",
  maxLabel = "Max",
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  inputType = "number",
  unit,
}: FilterRangeSectionProps) {
  const hasValue = !!minValue || !!maxValue;
  const minId = useId();
  const maxId = useId();

  return (
    <AccordionItem
      variant="plain"
      value={title}
      title={
        <>
          {title}
          {hasValue && <CountBadge count={1} />}
        </>
      }
    >
      <div className={styles.rangeRow}>
        <div>
          <Text as="label" variant="deci" color="muted" htmlFor={minId}>
            {minLabel}
            {unit ? ` (${unit})` : ""}
          </Text>
          <TextInputStandalone
            id={minId}
            type={inputType}
            className={styles.rangeInput}
            value={minValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onMinChange(e.target.value)
            }
          />
        </div>
        <div>
          <Text as="label" variant="deci" color="muted" htmlFor={maxId}>
            {maxLabel}
            {unit ? ` (${unit})` : ""}
          </Text>
          <TextInputStandalone
            id={maxId}
            type={inputType}
            className={styles.rangeInput}
            value={maxValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onMaxChange(e.target.value)
            }
          />
        </div>
      </div>
    </AccordionItem>
  );
}
